import React, { useRef } from 'react';
import { Helmet } from "react-helmet";
const BookAppointment = () => {
    const formRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData(formRef.current);
        // Get all fields as a plain object
        const data = Object.fromEntries(formData.entries());

        // Services interested (checkbox group) -> array
        const services = formData.getAll('servicesInterested');
        if (services.length) data.servicesInterested = services;

        // Simple anti-bot check
        if ((data.mathChallenge || '').trim() !== '18') {
            alert('Please answer the math challenge correctly (15 + 3 = 18).');
            return;
        }

        // Optional metadata
        const payload = {
            ...data,
            _subject: 'New Garage Growth Consultation (mmautotech.com)',
            _page: 'BookAppointment',
        };

        try {
            const response = await fetch('https://formspree.io/f/xzzvlaej', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                alert('Thanks! Your consultation request has been sent. Our team will contact you to confirm your slot.');
                formRef.current.reset();
            } else {
                alert('There was an error submitting your request. Please try again.');
            }
        } catch (err) {
            console.error('Booking error:', err);
            alert('There was an error submitting your request. Please try again.');
        }
    };

    return (
        <><Helmet>
            <title>Book Appointment | MM AutoTech</title>
            <meta
                name="description"
                content="Book a free consultation with MM AutoTech to discuss your garage growth plan and services."
            />
            <link rel="canonical" href="https://mmautotech.com/book-appointment" />
        </Helmet>
            <div className="bg-gray-50">
                {/* Hero */}
                <div className="relative w-full h-[420px] md:h-[500px]">
                    <img
                        src="/assets/Book_Appointment/1.jpeg" // use public/assets (no import needed)
                        alt="MM AutoTech — Garage growth and support services"
                        className="w-full h-full object-cover"
                        loading="lazy" />
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-center px-4">
                        <h1 className="text-white text-4xl md:text-5xl font-bold mb-4">
                            Book Your Free Garage Growth Consultation
                        </h1>
                        <p className="text-gray-200 text-lg md:text-xl max-w-3xl">
                            MM AutoTech helps UK garages grow with digital marketing, call handling, parts procurement,
                            and strategy consulting. Tell us about your garage and we’ll tailor a plan to boost bookings and margins.
                        </p>
                    </div>
                </div>

                {/* Booking Form */}
                <section className="max-w-4xl mx-auto p-6 bg-white shadow-md rounded-lg mt-10 mb-12">
                    <h2 className="text-2xl font-bold mb-2 text-center text-blue-900">
                        Request a Strategy Session
                    </h2>
                    <p className="text-center text-gray-600 mb-6">
                        Share a few details and we’ll confirm your consultation time. No sales pressure — just practical advice
                        on growing your garage with MM AutoTech.
                    </p>

                    <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                        {/* Contact Details */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block font-semibold mb-1">Full Name</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    required
                                    placeholder="Your name"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Role</label>
                                <input
                                    type="text"
                                    name="role"
                                    placeholder="Owner / Manager / Partner"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                        </div>

                        {/* Business Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block font-semibold mb-1">Garage Business Name</label>
                                <input
                                    type="text"
                                    name="businessName"
                                    required
                                    placeholder="e.g., PMS London"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Garage Location</label>
                                <input
                                    type="text"
                                    name="location"
                                    required
                                    placeholder="City / Postcode"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                        </div>

                        {/* Contact Methods */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block font-semibold mb-1">Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    required
                                    placeholder="+44 7xxx xxxxxx"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    placeholder="you@yourgarage.co.uk"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                        </div>

                        {/* Size & Website */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-1">
                                <label className="block font-semibold mb-1">Team Size</label>
                                <select name="teamSize" className="w-full border border-gray-300 px-4 py-2 rounded-md">
                                    <option value="">Select</option>
                                    <option value="1-3">1–3</option>
                                    <option value="4-7">4–7</option>
                                    <option value="8-12">8–12</option>
                                    <option value="13+">13+</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block font-semibold mb-1">Website (optional)</label>
                                <input
                                    type="url"
                                    name="website"
                                    placeholder="https://yourgarage.co.uk"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                        </div>

                        {/* Service Interests */}
                        <div>
                            <label className="block font-semibold mb-2">Which services are you interested in?</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {[
                                    'Local SEO & Google Maps',
                                    'Google/Facebook Ads',
                                    'Website Build/Redesign',
                                    'Inbound Call Handling & Admin',
                                    'Parts Procurement & Supplier Network',
                                    'Business Strategy & Coaching',
                                    'Other',
                                ].map((svc) => (
                                    <label key={svc} className="flex items-center space-x-2">
                                        <input type="checkbox" name="servicesInterested" value={svc} className="text-blue-600" />
                                        <span>{svc}</span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Current Bookings & Goals (optional) */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block font-semibold mb-1">Average Monthly Jobs (optional)</label>
                                <input
                                    type="number"
                                    name="currentMonthlyJobs"
                                    placeholder="e.g., 120"
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Primary Growth Goal (optional)</label>
                                <select name="primaryGoal" className="w-full border border-gray-300 px-4 py-2 rounded-md">
                                    <option value="">Select a goal</option>
                                    <option value="More calls & bookings">More calls & bookings</option>
                                    <option value="Improve online visibility">Improve online visibility</option>
                                    <option value="Reduce admin workload">Reduce admin workload</option>
                                    <option value="Lower parts cost">Lower parts cost</option>
                                    <option value="Overall growth strategy">Overall growth strategy</option>
                                </select>
                            </div>
                        </div>

                        {/* Preferred Consultation Date & Time */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block font-semibold mb-1">Preferred Date</label>
                                <input
                                    type="date"
                                    name="preferredDate"
                                    required
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                            </div>
                            <div>
                                <label className="block font-semibold mb-1">Preferred Time</label>
                                <select
                                    name="preferredTime"
                                    required
                                    className="w-full border border-gray-300 px-4 py-2 rounded-md"
                                >
                                    <option value="">Select a time</option>
                                    {[
                                        '09:00 AM', '10:30 AM', '11:00 AM', '11:30 AM', '12:00 PM',
                                        '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
                                        '3:30 PM', '4:00 PM', '5:30 PM',
                                    ].map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block font-semibold mb-1">Anything else we should know? (optional)</label>
                            <textarea
                                name="notes"
                                rows="3"
                                placeholder="Tell us about current challenges, systems used, or timing preferences."
                                className="w-full border border-gray-300 px-4 py-2 rounded-md" />
                        </div>

                        {/* Math Challenge */}
                        <div>
                            <label className="block font-semibold mb-2">Math Challenge</label>
                            <div className="flex items-center space-x-2">
                                <span className="px-3 py-2 bg-gray-100 border rounded">15 + 3 =</span>
                                <input
                                    type="text"
                                    name="mathChallenge"
                                    placeholder="Your Answer"
                                    required
                                    className="border border-gray-300 px-4 py-2 rounded-md w-24" />
                            </div>
                        </div>

                        {/* Honeypot for bots */}
                        <input type="text" name="_gotcha" className="hidden" tabIndex="-1" autoComplete="off" />

                        {/* Submit */}
                        <div className="text-center">
                            <button
                                type="submit"
                                className="bg-green-600 hover:bg-green-700 text-white font-semibold px-8 py-3 rounded-md"
                            >
                                Book My Free Strategy Call
                            </button>
                        </div>
                    </form>
                </section>
            </div></>
    );
};

export default BookAppointment;
